import { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints, PaymentStepConfig } from '@/api';
import AlertBar from '@/design_system/AlertBar';
import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInfo from '@/icons/Info.svg';
import { isInPaymentStep, RequestWithRelations } from '@/models/request';
import { TransactionFromGet } from '@/models/transaction';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { Currency, formatCurrency } from '@/utils/number';

import { FEE_TYPE_LABELS } from './PriceWithDetails/usePriceDetailsRows';

type Prices =
  | Endpoints['GET /requests/:id/client-amount-due']['response']['prices']
  | TransactionFromGet['invoiceLines'][number]['price'][];

export const ClientTransactionDetails = ({ prices }: { prices: Prices }) => {
  const { t, i18n } = useLingui();

  const servicesAmount = prices
    .filter((price) => !price.fee && !price.deposit && !price.creditTransactions.length)
    .reduce((acc, curr) => acc + curr.amount, 0);
  const handlingFeeAmount = prices
    .filter((price) => price.fee?.feeType.slug === 'client-handling')
    .reduce((acc, curr) => acc + curr.amount, 0);
  const shippingManagementFeeAmount = prices
    .filter((price) => price.fee?.feeType.slug === 'client-shipping-management')
    .reduce((acc, curr) => acc + curr.amount, 0);
  const paidEstimateAdvanceFeeAmount = prices
    .filter((price) => price.fee?.feeType.slug === 'paid-estimate-advance')
    .reduce((acc, curr) => acc + curr.amount, 0);
  const depositAmount = prices
    .filter((price) => !!price.deposit)
    .reduce((acc, curr) => acc + curr.amount, 0);
  const paidAmount = prices
    .filter((price) => !!price.creditTransactions.length)
    .reduce((acc, curr) => acc + curr.amount, 0);
  const totalAmount = prices.reduce((acc, curr) => acc + curr.amount, 0);

  const currency = prices[0].currency;

  const showDepositLine = prices.some((price) => !!price.deposit);
  const deposit = prices.find((price) => !!price.deposit)?.deposit;
  const depositType = deposit?.type;
  const depositPercentage = deposit?.percentage;

  return (
    <Stack gap="0.125rem">
      {!!servicesAmount && (
        <PriceLine
          label={<Trans id="client-transaction-details.services.label">Services</Trans>}
          amount={servicesAmount}
          currency={currency}
        />
      )}
      {showDepositLine && (
        <PriceLine
          label={
            <>
              {depositType === 'percentage' ? (
                <>
                  <Trans id="client-transaction-details.deposit.label">Deposit</Trans>{' '}
                  <Trans id="client-transaction-details.deposit.percentage">
                    ({depositPercentage}% of total services)
                  </Trans>
                </>
              ) : (
                <Trans id="client-transaction-details.paid-estimate-advance.label">
                  Paid estimate advance
                </Trans>
              )}
            </>
          }
          amount={depositAmount}
          currency={currency}
        />
      )}
      {!!handlingFeeAmount && (
        <PriceLine
          label={i18n._(FEE_TYPE_LABELS['client-handling'])}
          amount={handlingFeeAmount}
          currency={currency}
        />
      )}
      {!!shippingManagementFeeAmount && (
        <PriceLine
          label={i18n._(FEE_TYPE_LABELS['client-shipping-management'])}
          amount={shippingManagementFeeAmount}
          currency={currency}
        />
      )}
      {!!paidEstimateAdvanceFeeAmount && (
        <PriceLine
          label={i18n._(FEE_TYPE_LABELS['paid-estimate-advance'])}
          amount={paidEstimateAdvanceFeeAmount}
          currency={currency}
        />
      )}
      {!!paidAmount && (
        <PriceLine
          label={<Trans id="client-transaction-details.amount-paid.label">Amount paid</Trans>}
          amount={paidAmount}
          currency={currency}
        />
      )}
      <PriceLine
        variant="total"
        label={
          <>
            <Trans id="client-transaction-details.total.label">Total</Trans>{' '}
            <InlineText size="200">
              ({t({ id: '_general.incl-vat', message: 'incl. VAT' })})
            </InlineText>
          </>
        }
        amount={totalAmount}
        currency={currency}
      />
    </Stack>
  );
};

const PriceLine = ({
  label,
  amount,
  currency,
  variant = 'default',
}: {
  label: ReactNode;
  amount: number;
  currency: Currency;
  variant?: 'default' | 'total';
}) => {
  const weight = variant === 'total' ? 'medium' : undefined;
  const size = variant === 'total' ? '100' : '200';

  return (
    <Stack row justifyContent="space-between">
      <InlineText size={size} weight={weight}>
        {label}
      </InlineText>
      <InlineText size={size} weight={variant === 'total' ? 'medium' : undefined}>
        {' '}
        {formatCurrency(amount, currency)}
      </InlineText>
    </Stack>
  );
};

export const ClientTransactionPartialPaymentWarning = ({ prices }: { prices: Prices }) => {
  const { request } = useRequestContext();

  const totalAmountToPay = prices.reduce((acc, curr) => acc + curr.amount, 0);
  const totalAmount = request.organizationPrice?.amountPerCurrency?.[0]?.amount;
  const currency = prices[0].currency;

  const remainingDueAmount = totalAmount ? totalAmount - totalAmountToPay : null;
  const remainingDue = remainingDueAmount ? formatCurrency(remainingDueAmount, currency) : null;

  const requestHasPaidEstimateAdvance = prices.some(
    (price) => price.deposit?.type === 'paid-estimate-advance'
  );

  if (requestHasPaidEstimateAdvance) {
    return (
      <Stack row gap="0.25rem" alignItems="flex-start" flexWrap="nowrap">
        <IconInfo style={{ fontSize: '1rem', color: 'var(--color-tertiary-700)' }} />
        <InlineText color="secondary" size="200">
          <Trans id="client-transaction-details.paid-estimate-advance">
            The paid estimate advance is non-refundable if the proposed quote is declined.
            Additional charges may apply after further assessment.
          </Trans>
        </InlineText>
      </Stack>
    );
  }

  return (
    <Stack row gap="0.25rem" alignItems="flex-start" flexWrap="nowrap">
      <IconInfo style={{ fontSize: '1rem', color: 'var(--color-tertiary-700)' }} />
      <InlineText color="secondary" size="200">
        {!!remainingDue && (
          <>
            <Trans id="client-transaction-details.remaining-balance">
              Remaining balance: {remainingDue} (charged later).
            </Trans>{' '}
          </>
        )}
        <Trans id="client-transaction-details.remaining-balance.additional-charges">
          Additional charges may apply after further assessment.
        </Trans>
      </InlineText>
    </Stack>
  );
};

export const isFinalPaymentStep = (request: RequestWithRelations) => {
  if (!isInPaymentStep(request)) {
    return false;
  }

  const paymentStepConfig = request.articles.find((article) => article.step?.step === 'payment')
    ?.step as PaymentStepConfig | undefined;

  return !paymentStepConfig?.config.priceTypesToPay?.length;
};

export const PaymentModalPriceDetails = ({
  prices,
  showLabel,
}: {
  prices: Prices;
  showLabel?: boolean;
}) => {
  const { request } = useRequestContext();

  const isPartialPayment = isInPaymentStep(request) && !isFinalPaymentStep(request);

  if (!prices.length) {
    return null;
  }

  return (
    <Stack gap="0.5rem">
      <Box
        style={{
          backgroundColor: 'var(--color-neutral-200)',
          borderColor: 'var(--color-neutral-300)',
        }}
        padding="12px 16px"
        gap="0.25rem"
      >
        {showLabel && (
          <Paragraph weight="medium">
            <Trans id="request.point-of-sales.price.label-due">Amount due</Trans>
          </Paragraph>
        )}
        <ClientTransactionDetails prices={prices} />
      </Box>
      {isPartialPayment && (
        <AlertBar hideIcon>
          <ClientTransactionPartialPaymentWarning prices={prices} />
        </AlertBar>
      )}
    </Stack>
  );
};
